<script setup lang="ts">
// Blogs
import { useRouter, useRoute } from 'vue-router'
import { ref, onMounted, computed, onUnmounted } from 'vue'
import useSecurityChecks from '~/composables/useSecurityChecks'

// Form submit
const toast = useToast()

const email = ref('')
const loading = ref(false)

// Add this after the existing refs
const route = useRoute()
// Initialize these as empty to prevent hydration mismatch
// They'll be populated in onMounted for client-side only
const referralCode = ref('')
const skipCode = ref('')
const showReferralBanner = ref(false)
const isSkipWaitlistCode = ref(false)
const codeVerified = ref(false)

// Add after other declarations
const { getSecurityData } = useSecurityChecks()

// Add hydration-safe initialization
const isHydrated = ref(false)

// Add interval ref for cleanup
const refreshInterval = ref<NodeJS.Timeout | null>(null)

// Clean up interval on unmount
onUnmounted(() => {
  if (refreshInterval.value) {
    clearInterval(refreshInterval.value)
  }
})

// Critical resource preloading removed - NuxtImg handles this with its preload prop
// No manual preload needed to avoid conflicts with format optimization

// NuxtImg handles preloading automatically with its preload prop
// No need for manual preload that conflicts with format optimization

// Define newsletter type
interface Newsletter {
  title: string
  description: string
  image: { src: string }
  date: string
  badge: { label: string }
  path: string
  categoryId: number
  slug: string
}

// Fetch newsletters from API with optimized settings for LCP
const { data: newslettersData, refresh: refreshNewsletters } = await useFetch<
  Newsletter[]
>('/api/newsletters/recent', {
  key: 'newsletters-recent', // Static key to prevent hydration mismatch
  server: true, // Ensure SSR for faster initial load
  lazy: false, // Don't lazy load to prevent layout shift
  transform: (data: Newsletter[]) => data || [], // Ensure we always have an array
  default: () => [], // Provide default value to prevent hydration mismatch
  // Aggressive cache busting for fresh data
  dedupe: 'cancel', // Cancel pending requests when new ones are made
})
const newsletters = computed(() => newslettersData.value || [])

//logos
const logos = [
  {
    name: 'ULA',
    path: '/logos/United_Launch_Alliance-Logo.wine.svg',
  },
  {
    name: 'Blue Origin',
    path: '/logos/bluori.svg',
  },
  {
    name: 'SpaceX',
    path: '/logos/SpaceX-Logo.svg',
  },

  {
    name: 'Lockheed Martin',
    path: '/logos/LMT.svg',
  },
  {
    name: 'Boeing',
    path: '/logos/Boeing_full_logo.svg',
  },
  {
    name: 'RTX',
    path: '/logos/RTX.svg',
  },
  {
    name: 'ESA',
    path: '/logos/European_Space_Agency_logo.svg',
  },
  {
    name: 'NASA',
    path: '/logos/nasa-inv.svg',
  },
  {
    name: 'JOBY Aviation',
    path: '/logos/JOBY.svg',
  },
  {
    name: 'US Air Force',
    path: '/logos/USAF.svg',
  },
  {
    name: 'US Space Force',
    path: '/logos/USSF.svg',
  },
  {
    name: 'Airbus',
    path: '/logos/Airbus_Logo_2017.svg',
  },
  {
    name: 'JAXA',
    path: '/logos/Jaxa_logo.svg',
  },
  {
    name: 'Northrop Grumman',
    path: '/logos/Northrop_Grumman_logo_blue-on-clear_2020.svg',
  },
]

//Testimonials

// Reviews data
const reviews = [
  {
    name: 'Emily Thompson',
    username: 'Aerospace Engineer',
    body: "I've subscribed to many newsletters, but this one stands out. The content is fresh, concise, and always relevant to my work.",
    img: '',
  },
  {
    name: 'Liam Johnson',
    username: 'Satellite Program Manager',
    body: "This newsletter keeps me ahead of the curve. I appreciate the focus on quality over quantity. It's a game changer!",
    img: '',
  },
  {
    name: 'James Carter',
    username: 'Avionics Specialist',
    body: 'Finally, a newsletter that gets straight to the point.',
    img: '',
  },
  {
    name: 'Sophia Martinez',
    username: 'Defense Analyst',
    body: "Great curation of news and insights. It's exactly what I need to stay on top of industry trends without spending hours reading.",
    img: '',
  },
  {
    name: 'Alexander Green',
    username: 'Space Policy Advisor',
    body: 'Wow! The coverage is always on point and the insights are sharp.',
    img: '',
  },
  {
    name: 'Olivia Brown',
    username: 'Flight Systems Developer',
    body: 'The best way to stay informed without feeling overwhelmed! Highly recommended.',
    img: '',
  },
  {
    name: 'Charlotte Wilson',
    username: 'Rocket Scientist',
    body: "Efficient, insightful, and truly bite-sized. It's the perfect addition to my busy mornings.",
    img: '',
  },
]

// Rows
const firstRow = ref(reviews)

///////////////////////////////////////////////////////////////////////////////
// Scroll up and input

const emailInput = ref(null) // Ref for the email input element
const router = useRouter()

// Add this composable at the top level
const focusInput = () => {
  // Only run on client after hydration
  if (!isHydrated.value || typeof window === 'undefined') return

  setTimeout(() => {
    const input = emailInput.value?.$el.querySelector('input')
    if (input) {
      input.focus()
      input.click()
      // Force scroll to top in case the page is scrolled
      window.scrollTo(0, 0)
      // Double-tap approach for stubborn mobile browsers
      setTimeout(() => {
        input.focus()
        input.click()
      }, 100)
    }
  }, 100)
}

// Updated verification function to handle both code types
async function verifyCodeType() {
  // Check if we have either type of code
  const codeToVerify = skipCode.value || referralCode.value
  if (!codeToVerify) return

  try {
    const response = await $fetch('/api/waitlist/verify-code', {
      method: 'GET',
      params: {
        code: codeToVerify,
        codeType: skipCode.value ? 'skip' : 'referral',
      },
    })

    if ((response as WaitlistResponse).success) {
      // We already set isSkipWaitlistCode based on URL param presence
      // Keep the banner visible only for valid codes
      showReferralBanner.value = true
    } else {
      // Code is invalid, hide banner and perform a hard redirect to homepage
      console.log(
        'Invalid code detected, redirecting to homepage with hard reload',
      )

      // First hide the banner to prevent flash of UI
      showReferralBanner.value = false

      // For skip codes, perform a hard reload to reset the UI state completely
      if (skipCode.value) {
        // Use window.location for a hard reload
        window.location.href = '/'
      } else {
        // For regular referral codes, client-side navigation is fine
        router.replace('/')
      }
    }
  } catch (error) {
    console.error('Error verifying code:', error)
    // On error, hide banner and redirect to homepage for safety
    showReferralBanner.value = false

    // Hard reload for errors with skip codes as well
    if (skipCode.value) {
      window.location.href = '/'
    } else {
      router.replace('/')
    }
  } finally {
    codeVerified.value = true
  }
}

// Updated onMounted function with enhanced hydration safety
onMounted(async () => {
  // Wait for complete hydration
  await nextTick()
  await new Promise((resolve) => setTimeout(resolve, 50))

  // Set hydration flag
  isHydrated.value = true

  // Now safe to run client-side logic
  focusInput()

  // Initialize query params client-side to prevent hydration mismatch
  referralCode.value = (route.query.code as string) || ''
  skipCode.value = (route.query.skip as string) || ''
  isSkipWaitlistCode.value = !!route.query.skip

  // Initially hide the banner until we verify the code
  showReferralBanner.value = false

  // Verify the code if we have either type
  if (referralCode.value || skipCode.value) {
    await verifyCodeType()
  }

  // Set up automatic newsletter refresh every 2 minutes to match cache expiration
  refreshInterval.value = setInterval(
    () => {
      refreshNewsletters()
    },
    5 * 60 * 1000,
  ) // 5 minutes

  // Watch for route changes to handle back/forward navigation
  router.afterEach((to) => {
    // Only focus input if we're staying on the homepage
    if (to.path === '/') {
      focusInput()
    }
  })

  // Handle page visibility changes (tab switching/reopening)
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        focusInput()
        // Also refresh newsletters when user returns to the tab
        refreshNewsletters()
      }
    })
  }
})

// Update scrollAndFocus function
const scrollAndFocus = async () => {
  if (!isHydrated.value || typeof window === 'undefined') return

  await new Promise((resolve) => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    setTimeout(resolve, 300)
  })
  focusInput()
}

// Format date consistently for SSR/client hydration
const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  // Use a more predictable format to avoid timezone differences
  return date.toLocaleDateString('en', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC', // Force UTC to prevent server/client differences
  })
}

const honeypot = ref('')

// TypeScript guard to check success property with explicit type checking
interface WaitlistResponse {
  success: boolean
  resuming?: boolean
  waitlistPosition?: number
  referralToken?: string
  uuid?: string
  skipCode?: string
  skippedWaitlist?: boolean
  position?: number
}

async function subscribe() {
  // Don't process if loading
  if (loading.value) {
    return
  }

  if (!email.value) {
    toast.add({
      title: 'Error',
      description: 'Please enter your email',
      color: 'red',
    })
    return
  }

  // Start with not resuming flow
  let isResumingFlow = false

  // Check for existing subscriptions via the API first
  const securityData = getSecurityData()
  try {
    // First check if this email exists in our system
    console.log('Checking subscription status for:', email.value)

    const statusCheck = await $fetch('/api/subscribe/check-status', {
      method: 'POST',
      body: {
        email: email.value,
        fingerprint: securityData.fp,
        flow_resumed: false, // Initial check with resumption off
      },
    })

    console.log('Subscription status check result:', statusCheck)

    // If email exists and is not active, we'll set resumption to true
    if (statusCheck.exists) {
      console.log('Email exists in system, checking if resumable:', {
        status: statusCheck.status,
        resumable: statusCheck.resumable,
        source: statusCheck.source,
      })

      // For active subscribers, block them
      if (statusCheck.status === 'active') {
        toast.add({
          title: 'Already subscribed',
          description: 'This email is already active in our system',
          color: 'primary',
        })
        return
      }
      // If this browser already subscribed with a different email, block them
      else if (
        statusCheck.status === 'browser_subscribed' &&
        statusCheck.subscribedEmail &&
        statusCheck.subscribedEmail !== email.value
      ) {
        toast.add({
          title: 'Already subscribed',
          description: `You've already subscribed with a different email`,
          color: 'primary',
        })
        return
      }
      // For pending cases, set isResumingFlow to true to resume their flow
      else if (statusCheck.resumable === true) {
        console.log('Resuming incomplete flow for:', email.value)
        isResumingFlow = true
      }
      // For explicitly completed/waitlisted subscriptions, block resumption
      else if (
        statusCheck.status === 'completed' ||
        statusCheck.status === 'waitlisted'
      ) {
        toast.add({
          title: 'Already subscribed',
          description: `This email has already completed the signup process`,
          color: 'primary',
        })
        return
      }
      // Any other case, try resuming anyway
      else {
        console.log('Status unclear, attempting resumption for:', email.value)
        isResumingFlow = true
      }
    }

    // Continue with normal flow or with resumption
  } catch (error) {
    console.error('Error checking subscription status:', error)
    // Continue anyway as the main endpoint will also check
  }

  loading.value = true

  try {
    // Prepare request payload - prioritize skipCode over referralCode
    const payload = {
      email: email.value,
      code: skipCode.value || referralCode.value || null,
      codeType: skipCode.value
        ? 'skip'
        : referralCode.value
          ? 'referral'
          : 'none',
      honeypot: honeypot.value,
      security: securityData,
      flow_resumed: isResumingFlow, // Use the determined value
    }

    console.log('Submitting to waitlist:', payload.email)

    // Call the waitlist join endpoint
    const response = await $fetch('/api/waitlist/join', {
      method: 'POST',
      body: payload,
    })

    console.log('Waitlist response:', response)

    // TypeScript guard to check success property with explicit type checking
    if (
      response &&
      typeof response === 'object' &&
      'success' in response &&
      (response as WaitlistResponse).success === true
    ) {
      // Flow identifier now tracked server-side via KV, no localStorage needed

      // Properly cast the response to our interface
      const typedResponse = response as WaitlistResponse

      // Prepare common query parameters
      const baseQuery = {
        email: email.value,
        category: 'aerospace',
        isMain: 'true',
        flow: 'homepage', // Add flow identifier in query param instead of localStorage
      }

      // Route based on whether waitlist was skipped or resumed
      if ('resuming' in typedResponse && typedResponse.resuming) {
        // We're resuming an existing flow
        console.log('Resuming existing flow with data:', typedResponse)

        if (
          'waitlistPosition' in typedResponse &&
          typedResponse.waitlistPosition
        ) {
          // This was a waitlist signup that wasn't completed
          router.push({
            path: '/subscribe',
            query: {
              ...baseQuery,
              position: String(typedResponse.waitlistPosition || 0),
              referralToken: String(typedResponse.referralToken || ''),
              uuid: String(typedResponse.uuid || ''),
            },
          })
        } else {
          // This was a direct signup that wasn't completed
          router.push({
            path: '/subscribe',
            query: {
              ...baseQuery,
              uuid: String(typedResponse.uuid || ''),
              skipCode: String(typedResponse.skipCode || ''),
            },
          })
        }
      } else if (
        'skippedWaitlist' in typedResponse &&
        typedResponse.skippedWaitlist
      ) {
        router.push({
          path: '/subscribe',
          query: {
            ...baseQuery,
            skippedWaitlist: 'true',
            uuid: String(typedResponse.uuid || ''),
            skipCode: String(typedResponse.skipCode || ''),
          },
        })
      } else {
        router.push({
          path: '/subscribe',
          query: {
            ...baseQuery,
            position:
              'position' in typedResponse
                ? String(typedResponse.position)
                : '0',
            referralToken:
              'referralToken' in typedResponse
                ? String(typedResponse.referralToken)
                : '',
          },
        })
      }
    }
  } catch (error) {
    console.error('Waitlist error:', error)

    // Check for subscription with different email
    if (error.status === 409 && error.data?.subscribedEmail) {
      toast.add({
        title: 'Already subscribed',
        description: `You've already subscribed`,
        color: 'primary',
      })
    } else {
      toast.add({
        title: 'Error',
        description: error.data?.message || 'Failed to join waitlist',
        color: 'red',
      })
    }
  } finally {
    loading.value = false
  }
}

// Add this to your existing data structure
const faq = {
  title: 'Still Skeptical? We Get It.',
  description:
    'Straight answers to what industry professionals really want to know',
  items: [
    {
      label: 'How is this different from other aerospace newsletters?',
      content:
        'Yes, you already read Aviation Week, SpaceNews, and AIAA—but do you have time to read them every day? Probably not. Unlike traditional newsletters that overwhelm you with lengthy content, AERO.NOW delivers carefully curated, AI-enhanced summaries and links of the most crucial aerospace developments in a 5-minute read. Our unique combination of industry expert curation and AI technology ensures you get maximum insight with minimum time investment.',
    },
    {
      label: 'How often is the newsletter sent out?',
      content: `The newsletter is delivered Monday through Friday every morning (timing adjusted to your region). This consistent weekday schedule ensures you're always up-to-date with the latest aerospace developments when you need them most.`,
    },
    // {
    //   label: "Who's behind AERO.NOW?",
    //   content: `Our team consists of aerospace industry veterans, former defense analysts, and tech experts who understand the complex dynamics of the industry. We're backed by advanced AI technology and supported by a network of industry insiders who help ensure our content is accurate, relevant, and valuable.`,
    // },
    {
      label: "Why is it free? What's the catch?",
      content: `There's no catch! We believe in democratizing access to quality aerospace intelligence. Our mission is to empower every professional in the industry with essential insights, regardless of their role or organization. We're able to maintain this by keeping our operations lean, leveraging cutting-edge AI technology, and focusing on what matters most: delivering exceptional value to our readers.`,
    },
    // {
    //   label: 'How many people read AERO.NOW?',
    //   content: `We're proud to serve thousands of industry leaders, including C-suite executives and senior decision-makers from SpaceX, Boeing, Lockheed Martin, NASA, and other leading aerospace organizations. Our subscriber base includes VPs, Directors, and Program Managers who rely on our insights for strategic planning and decision-making. We're growing rapidly as more professionals discover the value of our concise, insight-rich format.`,
    // },
    {
      label: 'Can I really unsubscribe anytime?',
      content: `Absolutely! While our retention rate is over 94% (something we're very proud of), you can unsubscribe with a single click at any time. No questions asked, no hoops to jump through.`,
    },
  ],
}
</script>

<template>
  <div>
    <!-- Updated referral banner with different text based on code type -->
    <ClientOnly fallback="">
      <div
        v-if="isHydrated && showReferralBanner"
        data-testid="referral-banner"
        class="bg-primary/10 border-b border-primary/20 text-primary py-2 px-4 flex items-center justify-center gap-1"
      >
        <UIcon
          name="i-heroicons-user-plus"
          class="inline-flex flex-shrink-0 self-center"
        />
        <span v-if="isSkipWaitlistCode">
          You've been invited to join <AppBrand /> immediately! Enter your email
          to skip the waitlist.
        </span>
        <span v-else>
          You've been invited to join our exclusive waitlist! Enter your email
          to secure your spot.
        </span>
      </div>
    </ClientOnly>

    <ULandingHero class="mb-0 pb-12 md:py-0 md:pt-40">
      <div
        class="absolute inset-0 landing-grid z-[-1] [mask-image:radial-gradient(100%_100%_at_top_right,white,transparent)]"
      />

      <template #headline>
        <ULandingSection class="w-full max-w-xl mx-auto py-0 pt-8 sm:py-0">
          <template #description>
            Get the free daily email trusted by executives and decision-makers
            in
            <span class="relative z-10 pointer-events-auto">
              <NuxtLink
                to="/newsletters"
                class="text-primary hover:underline"
                @click.stop
              >
                Aerospace, Astronautics & Defense
              </NuxtLink>
            </span>
            🚀
          </template>
          <template #headline>
            <form class="max-w-lg mx-auto" @submit.prevent="subscribe">
              <UFormGroup :ui="{ container: 'mt-3' }">
                <UInput
                  ref="emailInput"
                  v-model="email"
                  type="email"
                  placeholder="Enter your email"
                  :ui="{ icon: { trailing: { pointer: '' } } }"
                  required
                  size="xl"
                  autocomplete="off"
                  class="w-full"
                  input-class="rounded-full w-[300px] sm:w-[350px] border-2 border-primary focus:ring-primary focus:border-primary focus:outline-none text-truncate pr-[125px]"
                >
                  <template #trailing>
                    <UButton
                      type="submit"
                      size="xs"
                      color="primary"
                      :loading="loading"
                    >
                      <template v-if="loading"> Joining... </template>
                      <ClientOnly v-else fallback="Join Waitlist">
                        <template
                          v-if="
                            isHydrated && isSkipWaitlistCode && codeVerified
                          "
                        >
                          Skip Waitlist
                        </template>
                        <template v-else> Join Waitlist </template>
                      </ClientOnly>
                    </UButton>
                  </template>
                </UInput>
              </UFormGroup>
              <!-- Honeypot field - hidden from real users -->
              <input
                v-model="honeypot"
                type="text"
                name="contact-preference"
                aria-hidden="true"
                class="absolute top-0 left-0 h-0 w-0 -z-50 opacity-0 pointer-events-none"
                tabindex="-1"
                autocomplete="off"
              />
            </form>

            <p class="text-gray-500 mt-2 font-normal text-xs">
              <UIcon
                name="i-heroicons-shield-check"
                class="text-gray-400 translate-y-[1px]"
              />
              <!-- We take your privacy very seriously and never spam -->
              No spam. Unsubscribe at any time in one click.
            </p>
          </template>
        </ULandingSection>

        <ContainerScroll>
          <template #title>
            <h1
              className="text-5xl sm:text-5xl md:text-[4.5rem] lg:text-[5rem] xl:text-[5.5rem] font-light text-black dark:text-white uppercase"
            >
              Dominate Aerospace <br />
              <span
                className="text-3xl sm:text-5xl md:text-[4.5rem] lg:text-[5rem] xl:text-[5.5rem] font-black tracking-[0.035em]"
              >
                in 5 Minutes a Day
              </span>
            </h1>
          </template>
          <template #card>
            <NuxtImg
              src="/aeromail.png"
              class="mx-auto h-full rounded-2xl object-cover object-center"
              alt="AERO.NOW Newsletter Dashboard"
              width="1365"
              height="773"
              loading="eager"
              sizes="(max-width: 640px) 100vw, (max-width: 768px) 90vw, (max-width: 1024px) 80vw, (max-width: 1280px) 70vw, 1200px"
              format="webp,avif"
              quality="80"
              fetchpriority="high"
              densities="x1 x2"
            />
          </template>
        </ContainerScroll>

        <ULandingSection class="sm:py-0 sm:pt-6 sm:pb-16 py-0">
          <AnimatedLogoCloud :logos class="pb-8" />

          <p
            v-if="false"
            className="whitespace-pre-wrap font-light text-black dark:text-white text-3xl md:text-[4rem] leading-snug"
          >
            ...together with more than
            <NumberTicker :value="5000" /> <br />
            industry leaders and <br />
            decision-makers.
          </p>

          <p
            className="whitespace-pre-wrap font-light text-black dark:text-white uppercase max-w-4xl mx-auto text-4xl sm:text-5xl md:text-[5rem] lg:text-[5rem] xl:text-[5rem]"
          >
            Alongside the <span class="font-black">Leaders Shaping It</span>.
          </p>
        </ULandingSection>
      </template>
    </ULandingHero>

    <UContainer>
      <UPageBody class="m:py-0 sm:pb-32">
        <UBlogList>
          <!-- Render newsletters if we have data - available during SSR for SEO -->
          <template v-if="newsletters && newsletters.length > 0">
            <UBlogPost
              v-for="(newsletter, index) in newsletters"
              :key="newsletter.slug || index"
              :title="newsletter.title"
              :description="newsletter.description"
              :image="newsletter.image"
              :badge="newsletter.badge"
              :orientation="index === 0 ? 'horizontal' : 'vertical'"
              :class="[
                index === 0 && 'col-span-full',
                'cursor-pointer',
                'group',
              ]"
              variant="naked"
              :ui="{
                description: 'line-clamp-3',
              }"
              @click.stop="navigateTo(newsletter.path)"
            >
              <template #image>
                <NuxtImg
                  :src="newsletter.image.src"
                  :alt="newsletter.title"
                  :sizes="
                    index === 0
                      ? 'mobile:380px sm:592px md:592px lg:592px xl:592px'
                      : 'mobile:380px sm:384px md:384px lg:384px xl:384px'
                  "
                  format="webp,avif"
                  quality="50"
                  loading="lazy"
                  densities="x1 x2"
                  class="w-full h-full object-cover transform transition-transform duration-200 group-hover:scale-110"
                />
              </template>
              <template #date>
                <div class="flex items-center justify-between w-full">
                  <span>
                    {{ formatDate(newsletter.slug) }}
                  </span>
                  <UButton
                    label="Subscribe"
                    icon="i-heroicons-rss"
                    color="primary"
                    variant="ghost"
                    @click.stop="
                      navigateTo(`/${newsletter.path.split('/')[1]}`)
                    "
                  />
                </div>
              </template>
            </UBlogPost>
          </template>

          <!-- Loading fallback - only show when no data available -->
          <template v-else>
            <!-- First item - horizontal layout (col-span-full) -->
            <div class="col-span-full">
              <div class="flex gap-6 animate-pulse">
                <!-- Image placeholder -->
                <div
                  class="w-48 h-32 bg-gray-200 dark:bg-gray-700 rounded-lg flex-shrink-0"
                ></div>
                <!-- Content placeholder -->
                <div class="flex-1 space-y-3">
                  <!-- Badge -->
                  <div
                    class="w-24 h-5 bg-gray-200 dark:bg-gray-700 rounded"
                  ></div>
                  <!-- Title -->
                  <div
                    class="w-3/4 h-6 bg-gray-200 dark:bg-gray-700 rounded"
                  ></div>
                  <!-- Description lines -->
                  <div
                    class="w-full h-4 bg-gray-200 dark:bg-gray-700 rounded"
                  ></div>
                  <div
                    class="w-5/6 h-4 bg-gray-200 dark:bg-gray-700 rounded"
                  ></div>
                  <!-- Date -->
                  <div
                    class="w-20 h-4 bg-gray-200 dark:bg-gray-700 rounded"
                  ></div>
                </div>
              </div>
            </div>

            <!-- Remaining 6 items - vertical layout -->
            <div
              v-for="i in 6"
              :key="`skeleton-${i}`"
              class="animate-pulse space-y-3"
            >
              <!-- Image placeholder -->
              <div
                class="w-full h-32 bg-gray-200 dark:bg-gray-700 rounded-lg"
              ></div>
              <!-- Badge -->
              <div class="w-24 h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
              <!-- Title -->
              <div
                class="w-full h-5 bg-gray-200 dark:bg-gray-700 rounded"
              ></div>
              <!-- Description lines -->
              <div
                class="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded"
              ></div>
              <div class="w-4/5 h-3 bg-gray-200 dark:bg-gray-700 rounded"></div>
              <!-- Date -->
              <div class="w-16 h-3 bg-gray-200 dark:bg-gray-700 rounded"></div>
            </div>
          </template>
        </UBlogList>
      </UPageBody>
    </UContainer>

    <ULandingSection
      description="Get the aerospace intelligence others are missing—in just 5 minutes daily"
      icon="i-heroicons-square-3-stack-3d"
      class="py-0 sm:py-0 sm:pb-32 text-center"
    >
      <template #title>
        <span class="uppercase">Not Just Another Newsletter</span>
      </template>

      <UPageGrid>
        <ULandingCard
          title="Your Daily 5-Minute Power Boost"
          description="Stay ahead with top aerospace and defense news—curated, concise, and delivered every morning."
          icon="i-heroicons-clock"
        />
        <ULandingCard
          title="Curated by Experts, Perfected by AI"
          description="Our secret? Advanced AI and vetted industry experts carefully filter out the noise so you only get the most vital updates."
          icon="i-heroicons-cpu-chip"
        />
        <ULandingCard
          title="Totally Free, Absolutely No Fluff"
          description="High-value content, zero spam—your inbox deserves only the best, and that's exactly what we deliver."
          icon="i-heroicons-envelope"
        />
        <ULandingCard
          title="Exclusive Insider Access"
          description="Gain unprecedented perspective with executive insights, expert commentary, and behind-the-scenes analysis you won't find in mainstream publications."
          icon="i-heroicons-key"
        />
        <ULandingCard
          title="Career-Advancing Insights"
          description="Access the intelligence that helps you make smarter decisions, contribute more meaningfully in meetings, and advance your professional standing."
          icon="i-heroicons-briefcase"
        />
        <ULandingCard
          title="One Newsletter to Rule Them All"
          description="Stop juggling Aviation Week, SpaceNews, and AIAA. We aggregate all essential content from top industry sources into one concise, comprehensive digest."
          icon="i-heroicons-eye"
        />

        <!-- <ULandingCard
          title="AI-Enhanced Intelligence"
          description="Our proprietary AI analyzes thousands of sources to surface critical insights human curators might miss."
          icon="i-heroicons-cpu-chip"
        />
        <ULandingCard
          title="Expert Network Access"
          description="Benefit from our vast network of industry insiders who provide exclusive commentary and analysis."
          icon="i-heroicons-user-group"
        />
        <ULandingCard
          title="Technical Deep Dives"
          description="Complex aerospace innovations explained in clear, actionable terms—no PhD required."
          icon="i-heroicons-rocket-launch"
        />
        <ULandingCard
          title="Market Intelligence"
          description="Track industry deals, funding rounds, and strategic moves that impact your business decisions."
          icon="i-heroicons-chart-bar"
        />
        <ULandingCard
          title="Policy & Regulation Alerts"
          description="Stay compliant with real-time updates on aerospace regulations, policies, and global standards."
          icon="i-heroicons-document-text"
        />
        <ULandingCard
          title="Zero Noise Guarantee"
          description="Every story earns its place. No fluff, no PR spin—just what matters to aerospace pros."
          icon="i-heroicons-shield-check"
        /> -->
      </UPageGrid>
    </ULandingSection>

    <ULandingSection
      icon="i-heroicons-question-mark-circle"
      :description="faq.description"
      class="py-0 md:py-0 pt-24 sm:pt-0"
    >
      <template #title>
        <span class="uppercase max-w-[300px] sm:max-w-none inline-block">{{
          faq.title
        }}</span>
      </template>
      <ULandingFAQ :items="faq.items" single default-closed class="mx-auto" />
    </ULandingSection>

    <ULandingSection v-if="false">
      <ULandingCTA
        title="Ready to dominate aerospace?"
        description="Join 5,000+ industry professionals getting smarter every day."
        :links="[
          {
            label: 'Subscribe Free',
            to: '#',
            icon: 'i-heroicons-rocket-launch',
            color: 'primary',
            click: scrollAndFocus,
          },
          {
            label: 'Read Sample Issue',
            to: '/sample',
            icon: 'i-heroicons-document-text',
          },
        ]"
        :card="false"
        class="bg-gray-100/50 dark:bg-gray-800/50"
      />
    </ULandingSection>

    <ULandingSection>
      <div class="flex w-full max-w-7xl flex-col items-center justify-center">
        <div
          class="relative h-[430px] w-full overflow-hidden rounded-xl bg-white shadow-lg dark:bg-background"
        >
          <!-- Center Text -->
          <div
            class="absolute inset-0 z-10 mt-0 flex flex-col items-center justify-center px-4 text-center"
          >
            <h3
              class="mb-2 text-3xl font-bold sm:text-4xl uppercase text-shadow"
            >
              Stay Ahead of the Competition 🚀
            </h3>
            <p class="m-4 max-w-2xl text-base sm:text-lg text-shadow-sm">
              Bite-sized insights, expertly curated to give you the edge others
              wish they had. Don't let them take your spot.
            </p>
            <UButton
              label="👉 Subscribe Now — It's Free!"
              color="black"
              size="xl"
              class="lg:flex mt-4"
              @click="scrollAndFocus"
            />
          </div>

          <!-- Tilted Marquees -->
          <div class="absolute inset-0 overflow-hidden">
            <Marquee
              :style="{ transform: 'translateY(-11.5rem) rotate(-16deg)' }"
              class="marquee"
              :pause-on-hover="false"
            >
              <ReviewCard
                v-for="review in firstRow"
                :key="review.username"
                :img="review.img"
                :name="review.name"
                :username="review.username"
                :body="review.body"
              />
            </Marquee>

            <Marquee
              :style="{ transform: 'translateY(1rem) rotate(-16deg)' }"
              reverse
              class="marquee"
              :pause-on-hover="false"
            >
              <ReviewCard
                v-for="review in firstRow"
                :key="review.username"
                :img="review.img"
                :name="review.name"
                :username="review.username"
                :body="review.body"
              />
            </Marquee>

            <Marquee
              :style="{ transform: 'translateY(13.5rem) rotate(-16deg)' }"
              class="marquee"
              :pause-on-hover="false"
            >
              <ReviewCard
                v-for="review in firstRow"
                :key="review.username"
                :img="review.img"
                :name="review.name"
                :username="review.username"
                :body="review.body"
              />
            </Marquee>

            <Marquee
              :style="{ transform: 'translateY(26rem) rotate(-16deg)' }"
              reverse
              class="marquee"
              :pause-on-hover="false"
            >
              <ReviewCard
                v-for="review in firstRow"
                :key="review.username"
                :img="review.img"
                :name="review.name"
                :username="review.username"
                :body="review.body"
              />
            </Marquee>
          </div>

          <!-- Gradient overlay to fade to white at the bottom -->
          <div
            class="pointer-events-none absolute inset-0 bg-gradient-to-t from-white to-transparent dark:from-background"
          ></div>
        </div>
      </div>
    </ULandingSection>
  </div>
</template>

<style scoped>
.landing-grid {
  background-size: 100px 100px;
  background-image:
    linear-gradient(to right, rgb(var(--color-gray-200)) 1px, transparent 1px),
    linear-gradient(to bottom, rgb(var(--color-gray-200)) 1px, transparent 1px);
}
.dark {
  .landing-grid {
    background-image:
      linear-gradient(
        to right,
        rgb(var(--color-gray-800)) 1px,
        transparent 1px
      ),
      linear-gradient(
        to bottom,
        rgb(var(--color-gray-800)) 1px,
        transparent 1px
      );
  }
}

.marquee {
  --duration: 40s;
  position: absolute;
  left: -10%;
  width: 120%;
  padding: 0.5rem 0;
}

/* Ensure the outer anchor does not override the inner custom link */
.content-wrapper {
  position: relative;
  z-index: 1; /* Place above the outer anchor */
}

.custom-link {
  position: relative;
  z-index: 2; /* Ensure highest priority */
}

.description-wrapper {
  pointer-events: none;
}
.description-wrapper a {
  pointer-events: auto;
}

.text-shadow {
  text-shadow: 0 2px 4px rgba(0, 0, 0, 1);
}

.text-shadow-sm {
  text-shadow: 0 1px 2px rgba(0, 0, 0, 1);
}
</style>
